import { useLingui } from "@lingui/react/macro";
import { Clock, TriangleAlert, PauseCircle, XCircle } from "lucide-react";
import { useCookingSessionStats } from "@/hooks/cooking-sessions/queries";
import { MetricCard } from "@/components/common/MetricCard";
import type { CookingSessionSource } from "@/types/cooking-session";

interface WorkingStatsBarProps {
  locationId?: number;
  source?: CookingSessionSource;
}

export function WorkingStatsBar({ locationId, source }: WorkingStatsBarProps) {
  const { t } = useLingui();
  const { data: stats, isLoading } = useCookingSessionStats({ locationId, source });

  // Say which unit this is counting (T-1103). Orders counts tickets, this
  // counts dishes, and both labels read "Cooking" — so an order of three dishes
  // showed 3 here and 1 there with nothing to explain it. Prep queued from the
  // deck belongs to no ticket, which is why Orders cannot see it at all.
  const dishes = stats?.cookingNow ?? 0;
  const tickets = stats?.cookingTickets ?? 0;
  const prep = stats?.prepOnly ?? 0;
  const cookingSubtext =
    prep > 0
      ? t`${dishes} dishes · ${tickets} tickets · ${prep} prep`
      : t`${dishes} dishes across ${tickets} tickets`;

  return (
    <div className="mx-3 my-3 flex gap-2 rounded-xl bg-(--color-surface-muted) p-3">
      <MetricCard
        icon={Clock}
        label={t`Cooking now`}
        value={stats?.cookingNow}
        subtext={cookingSubtext}
        loading={isLoading}
      />
      <MetricCard
        icon={TriangleAlert}
        label={t`On deck`}
        value={stats?.onDeck}
        subtext={t`${stats?.onDeck ?? 0} pending`}
        loading={isLoading}
      />
      <MetricCard
        icon={PauseCircle}
        label={t`Late / Paused`}
        value={stats?.latePaused}
        subtext={t`Attention required`}
        loading={isLoading}
      />
      <MetricCard
        icon={XCircle}
        label={t`Unassigned`}
        value={stats?.unassigned}
        subtext={t`Needs chefs`}
        loading={isLoading}
      />
    </div>
  );
}
